import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { FaShareAlt, FaUserFriends, FaUsers } from 'react-icons/fa';
import { fetchUserConnections } from '../../../state/connectionSlice';
import calendarService from '../../../services/calendarService';

export default function ShareEventDialog({ 
  isOpen, 
  onClose, 
  event, 
  onEventShared 
}) {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const connections = useSelector(state => state.connections.connections);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [connectionType, setConnectionType] = useState('family');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user connections when the dialog is opened
  useEffect(() => {
    if (isOpen && user?.id) {
      setIsLoading(true);
      // Fetch all connection types
      dispatch(fetchUserConnections({ userId: user.id, type: 'all' }))
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
      
      // Reset selections when dialog opens
      setSelectedConnections([]);
      setError(null);
      setSuccessMessage('');
    }
  }, [isOpen, user?.id, dispatch]);

  // Check if a connection is already shared with this event
  const isConnectionAlreadyShared = (connection) => {
    if (!event || !event.sharedWith || !Array.isArray(event.sharedWith)) {
      return false;
    }
    
    const connectionId = connection._id || connection.id || connection.connectedUserId;
    
    return event.sharedWith.some(share => {
      const userId = share.userId || (share.user && share.user.id);
      return userId === connectionId;
    });
  };

  // Get connections for the selected type
  const getConnectionsForType = (type) => {
    if (!connections) return [];
    
    switch(type) {
      case 'family':
        return connections.family || [];
      case 'friend':
        return connections.friends || [];
      case 'other':
        return connections.business || [];
      case 'all':
        return [
          ...(connections.family || []),
          ...(connections.friends || []),
          ...(connections.business || [])
        ];
      default:
        return [];
    }
  };

  // Get the filtered connections based on the selected type
  const filteredConnections = getConnectionsForType(connectionType);

  const toggleConnectionSelection = (connection) => {
    const connectionId = connection._id || connection.id || connection.connectedUserId;
    
    setSelectedConnections(prev => 
      prev.includes(connectionId)
        ? prev.filter(id => id !== connectionId)
        : [...prev, connectionId]
    );
  };

  const isConnectionSelected = (connection) => {
    const connectionId = connection._id || connection.id || connection.connectedUserId;
    return selectedConnections.includes(connectionId);
  };

  const handleSubmit = async () => {
    if (!user?.id || !event?.id || selectedConnections.length === 0) {
      setError('Please select at least one connection');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Share event with all selected connections at once
      const result = await calendarService.shareEvent(event.id, selectedConnections);
      
      if (result.success) {
        setSuccessMessage(`Event shared successfully with ${result.successCount} user${result.successCount !== 1 ? 's' : ''}!`);
        
        // If there were any failures, show a message about them
        if (!result.allSucceeded) {
          setError(`Failed to share with ${result.totalCount - result.successCount} user${result.totalCount - result.successCount !== 1 ? 's' : ''}.`);
        }
        
        setTimeout(() => {
          setSuccessMessage('');
          if (onEventShared) {
            onEventShared(result);
          }
          
          if (onClose) {
            onClose();
          }
        }, 1500);
      } else {
        setError('Failed to share event. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Failed to share event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto hide-scrollbar"
            onClick={e => e.stopPropagation()}
          >
            {/* Glossy Card */}
            <div className="relative bg-gradient-to-b from-sec-dark/90 to-pri-dark/90 rounded-2xl shadow-2xl overflow-hidden border border-pri-white/10">
              {/* Glossy Effects */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/10 to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* Dialog Content */}
              <div className="relative p-5 sm:p-6">
                {/* Header with Title and Close Button */}
                <div className="flex justify-between items-center mb-6">
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-soft-pink to-mint-green"
                  >
                    Share Event
                  </motion.h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, rotate: 45 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    onClick={onClose}
                    className="rounded-full p-1 bg-pri-dark/50 text-pri-white hover:bg-pri-dark/80 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </motion.button>
                </div>

                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm"
                  >
                    {error}
                  </motion.div>
                )}
                
                {successMessage && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 text-sm"
                  >
                    {successMessage}
                  </motion.div>
                )}

                <div className="space-y-4">
                  {/* Event Info */}
                  <div className="p-3 bg-pri-dark/40 rounded-xl border border-pri-white/10">
                    <h3 className="font-medium text-soft-pink">{event?.title}</h3>
                  </div>
                  
                  {/* Connection Types */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                        <FaShareAlt className="h-4 w-4" />
                        <span>Share with</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
                      {['family', 'friend', 'other', 'all'].map(type => (
                        <motion.button
                          key={type}
                          type="button"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setConnectionType(type)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                            connectionType === type
                              ? 'bg-soft-pink/20 text-soft-pink border-soft-pink/30'
                              : 'bg-pri-dark/40 text-pri-white/70 border-pri-white/10 hover:bg-pri-dark/60'
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </motion.button>
                      ))}
                    </div>

                    {/* Connections List */}
                    <div className="bg-pri-dark/30 rounded-xl border border-soft-pink/10 max-h-60 overflow-y-auto p-2 space-y-1">
                      {isLoading ? (
                        <div className="flex justify-center items-center py-4">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                            className="h-5 w-5 border-2 border-soft-pink/30 border-t-soft-pink rounded-full"
                          />
                        </div>
                      ) : filteredConnections.length === 0 ? (
                        <div className="text-center py-3 text-pri-gray text-sm">
                          No connections found
                        </div>
                      ) : (
                        filteredConnections.map(connection => {
                          const isAlreadyShared = isConnectionAlreadyShared(connection);
                          return (
                            <motion.div
                              key={connection._id || connection.id || connection.connectedUserId}
                              whileHover={{ scale: isAlreadyShared ? 1 : 1.02, x: isAlreadyShared ? 0 : 3 }}
                              className={`flex items-center justify-between p-2 rounded-lg transition-all duration-200 ${
                                isAlreadyShared
                                  ? 'bg-pri-dark/20 border border-pri-white/5 opacity-60 cursor-not-allowed'
                                  : isConnectionSelected(connection)
                                    ? 'bg-soft-pink/20 border border-soft-pink/30 cursor-pointer'
                                    : 'bg-pri-dark/40 border border-pri-white/5 hover:bg-pri-dark/60 cursor-pointer'
                              }`}
                              onClick={() => !isAlreadyShared && toggleConnectionSelection(connection)}
                            >
                              <div className="flex items-center gap-2">
                                {connection.avatar ? (
                                  <img 
                                    src={connection.avatar} 
                                    alt={connection.name || 'User'} 
                                    className="h-6 w-6 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center text-xs text-white font-medium">
                                    {(connection.name && connection.name[0]) || 'U'}
                                  </div>
                                )}
                                <div className="text-sm text-pri-white">
                                  {connection.name || connection.username || 'User'}
                                  {isAlreadyShared && (
                                    <span className="ml-1 text-xs text-soft-pink italic">(Already shared)</span>
                                  )}
                                </div>
                              </div>
                              
                              {!isAlreadyShared && (
                                <div 
                                  className={`h-5 w-5 rounded-md flex items-center justify-center ${
                                    isConnectionSelected(connection)
                                      ? 'bg-soft-pink text-pri-dark'
                                      : 'bg-pri-dark border border-pri-white/30'
                                  }`}
                                >
                                  {isConnectionSelected(connection) && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              )}
                            </motion.div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3 pt-3">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={onClose}
                      className="px-5 py-2.5 rounded-xl bg-pri-dark/60 text-pri-white hover:bg-pri-dark transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)' }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleSubmit}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                      disabled={isSubmitting || selectedConnections.length === 0}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                            className="h-4 w-4 border-2 border-pri-dark/20 border-t-pri-dark rounded-full"
                          />
                          <span>Sharing...</span>
                        </div>
                      ) : (
                        `Share with ${selectedConnections.length} connection${selectedConnections.length !== 1 ? 's' : ''}`
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 